/**
 * Этап 4 / KS-07 — browser-side sanitizing for rendered rich text.
 *
 * The View mode used to render Markdown as React text segments and deliberately
 * carried NO `dangerouslySetInnerHTML` (DocumentEditor's docstring says so). HTML
 * content reverses that: showing it requires handing markup to the DOM, so the
 * safety has to come from a real sanitizer instead of from the absence of one.
 *
 * This is DEFENCE IN DEPTH, not the primary gate — `api/services/htmlSanitize.ts`
 * cleans on ingest and again on egress. It exists because the two run at different
 * moments: content stored BEFORE the server gate existed, or an artifact hand-edited
 * straight in the DB, reaches the screen without ever having passed it.
 *
 * The allow-list is the shared one (`@eer/shared/richText`), so the editor, this
 * renderer, the server gate and the `.docx` bridge cannot drift apart.
 */
import DOMPurify from 'dompurify';
import {
  RICH_TEXT_ALLOWED_TAGS,
  RICH_TEXT_ALLOWED_ATTRS,
  RICH_TEXT_ALLOWED_SCHEMES,
  dropEmptyListItems,
  normalizeRichText,
  renderFreeMarkdown,
  isInlineImageDataUri,
} from '@eer/shared';

/**
 * DOMPurify filters attributes globally, not per tag, so the per-tag policy is
 * flattened into one set. That is slightly LOOSER than the server gate (it would
 * tolerate `colspan` on a `<p>`), which is acceptable here: a misplaced layout
 * attribute is cosmetic, and the tag/scheme/handler restrictions — the parts that
 * actually matter — are unchanged.
 */
const ALLOWED_ATTR = [...new Set(Object.values(RICH_TEXT_ALLOWED_ATTRS).flat())];

/** `^(?:https?|mailto|cid):` built from the shared scheme list. */
const ALLOWED_URI_REGEXP = new RegExp(`^(?:${RICH_TEXT_ALLOWED_SCHEMES.join('|')}):`, 'i');

/**
 * Attributes that are NOT URLs and must be exempted from `ALLOWED_URI_REGEXP`.
 *
 * DOMPurify applies that regexp to EVERY attribute value, not just to `src`/`href` —
 * anything outside its internal `URI_SAFE_ATTRIBUTES` list is tested against it. The
 * built-in list covers `alt`, `title`, `style`… but not the layout attributes, so
 * `width="180"` failed `^(?:https?|mailto|cid):` and was silently dropped. Caught by
 * the test that asserted a signature logo keeps its width — without the exemption a
 * logo renders at full size in the client's email.
 */
const URI_SAFE_ATTR = ['width', 'height', 'colspan', 'rowspan'];

/**
 * Two post-pass fixes DOMPurify's config alone does not give us. Registered once —
 * DOMPurify hooks are global.
 *
 *  1. `ALLOWED_URI_REGEXP` is NOT the last word on `src`. DOMPurify keeps a separate
 *     internal `DATA_URI_TAGS` list (img, audio, video, source…) whose `data:` URIs
 *     bypass that regexp entirely — verified: `<img src="data:text/html;base64,…">`
 *     survived a config that allowed only http/https/mailto/cid. The shared policy
 *     excludes `data:` deliberately (mail clients strip it, so it is an injection
 *     surface bought for nothing), so the scheme is re-checked here, independently
 *     of DOMPurify's internal tables.
 *  2. Links rendered in the app open in a new tab; without `rel=noopener` that hands
 *     the opened page a reference back to our window (reverse tabnabbing).
 */
let hookInstalled = false;
function installHook(): void {
  if (hookInstalled) return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    const isImg = node.tagName === 'IMG';
    for (const attr of ['src', 'href'] as const) {
      const value = node.getAttribute?.(attr);
      if (value == null) continue;
      const trimmed = value.trim();
      if (ALLOWED_URI_REGEXP.test(trimmed)) continue;
      // The ONE `data:` exception (KS-06 ч.2): a raster image on an `<img src>`.
      // That is how a signature logo is stored — it renders here natively and is
      // converted to a `cid:` attachment when the email is sent. `data:image/svg+xml`
      // and `data:text/html` are NOT covered by `isInlineImageDataUri`; an SVG is a
      // document that can carry script, so it stays banned.
      if (isImg && attr === 'src' && isInlineImageDataUri(trimmed)) continue;
      node.removeAttribute(attr);
    }
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  hookInstalled = true;
}

/** Sanitize rich-text HTML for rendering in the app. Never throws. */
export function sanitizeForDisplay(html: unknown): string {
  const input = String(html ?? '');
  if (input.length === 0) return '';
  installHook();
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [...RICH_TEXT_ALLOWED_TAGS],
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    ADD_URI_SAFE_ATTR: URI_SAFE_ATTR,
    // `style` is allowed as an attribute above; DOMPurify's own CSS filter handles
    // the declarations, and it rejects `expression()`/`url(javascript:)` by default.
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
}

/**
 * What a display component actually needs: coerce whatever is stored (Markdown from
 * before the transition, HTML after it) into HTML, then sanitize it.
 *
 * Old Markdown values are rendered rather than shown raw — otherwise a document
 * written before the transition displays as literal `**bold**` on screen. Nothing
 * is written back, so this is a rendering decision, not a data migration.
 */
export function toDisplayHtml(value: unknown): string {
  // E03-F08-T06 — blank `<li>` from the model rendered as bullets with no text.
  return dropEmptyListItems(sanitizeForDisplay(normalizeRichText(value, renderFreeMarkdown)));
}
